/**
 * Deep equality check for CRM property values.
 *
 * Handles property order independence, empty value normalization, and arrays.
 * Used for change tracking in forms and seq collision conflict detection.
 */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }

  // Treat empty arrays as equivalent to null/undefined/empty string
  const isAEmpty =
    a === null ||
    a === undefined ||
    a === '' ||
    (Array.isArray(a) && a.length === 0);
  const isBEmpty =
    b === null ||
    b === undefined ||
    b === '' ||
    (Array.isArray(b) && b.length === 0);

  if (isAEmpty && isBEmpty) {
    return true;
  }
  if (isAEmpty || isBEmpty) {
    return false;
  }

  // Handle arrays (like multi_quantity)
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => valuesEqual(item, b[index]));
  }

  // Handle Date objects by comparing their time values
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (
    typeof a === 'object' &&
    typeof b === 'object' &&
    a !== null &&
    b !== null
  ) {
    // For objects, compare keys and values regardless of property order
    const keysA = Object.keys(a as Record<string, unknown>).sort();
    const keysB = Object.keys(b as Record<string, unknown>).sort();

    if (keysA.length !== keysB.length) {
      return false;
    }
    if (keysA.some((key, i) => key !== keysB[i])) {
      return false;
    }

    return keysA.every(key => {
      const valA = (a as Record<string, unknown>)[key];
      const valB = (b as Record<string, unknown>)[key];
      return valuesEqual(valA, valB);
    });
  }
  return String(a) === String(b);
}
